<template>
  <div class="musubi-page musubi-page--landing-pages">

    <!-- ============ BUILDER VIEW ============ -->
    <LandingPageBuilder
      v-if="view === 'builder' && builderPageId"
      :key="builderPageId"
      :page-id="builderPageId"
      :api-base="apiBase"
      :lists="lists"
      :pipelines="pipelines"
      :lead-forms="leadForms"
      :tenant-slug="tracking.tenant_slug || ''"
      @back="closeBuilder"
      @saved="refreshPages"
    />

    <!-- ============ HOME VIEW ============ -->
    <template v-else>
      <header class="lp-head">
        <div>
          <div class="lp-crumb">Tools <span>›</span> <b>Landing pages</b></div>
          <h1>Landing pages</h1>
          <p class="lp-lede">Capture pages that publish to your musubi.page subdomain and feed straight into a pipeline.</p>
        </div>
        <div class="lp-head-actions">
          <button class="lp-btn lp-btn--sec" @click="createBlank"><CygnusIcon name="plus" class="me-1" />Blank page</button>
        </div>
      </header>

      <div v-if="error" class="lp-alert lp-alert--danger">{{ error }}</div>

      <!-- === GUIDED: start from a pipeline (deterministic pre-fill, NOT AI) === -->
      <section class="lp-guide">
        <div class="lp-guide-l">
          <div class="lp-guide-top"><span class="lp-guide-icn"><CygnusIcon name="pin" /></span><h3>Start from a pipeline</h3></div>
          <p class="lp-guide-sub">Pick a pipeline and we pre-fill the page with its targeting — the audience is already aimed at the people that pipeline finds. Swap the template any time.</p>
          <div class="lp-guide-go">
            <button class="lp-btn lp-btn--pri" :disabled="!guidePipelineId || busy.create" @click="createFromPipeline">Use this pipeline <CygnusIcon name="arrow-right" class="ms-1" /></button>
            <span class="lp-guide-then">Opens in the builder with a hero template</span>
          </div>
        </div>
        <div class="lp-guide-r">
          <div class="lp-pick-lbl">Pipeline</div>
          <select class="lp-in" v-model="guidePipelineId">
            <option :value="null">— choose a pipeline —</option>
            <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
          <div v-if="guidePipeline" class="lp-prefill">
            <div class="lp-prefill-l">Aimed at</div>
            <div class="lp-prefill-h">{{ guidePipeline.audience_descriptor || guidePipeline.name }}</div>
            <div class="lp-prefill-note">Audience pulled from the pipeline’s targeting — edit it all in the builder.</div>
          </div>
          <p v-else-if="!pipelines.length" class="lp-hint mt-2">No pipelines yet. Create one under Pipelines first.</p>
        </div>
      </section>

      <!-- === TEMPLATE GALLERY === -->
      <div class="lp-sec-h"><h3>Templates</h3><span class="lp-cnt">{{ templates.length }} starting points · service-seller layouts</span></div>
      <div class="lp-tpl-grid">
        <div v-for="t in templates" :key="t.key" class="lp-tpl-card" @click="createFromTemplate(t)">
          <div class="lp-tpl-thumb">
            <div class="lp-skel">
              <span class="sk-d" style="width:80%"></span>
              <span class="sk-d" style="width:60%"></span>
              <span class="sk" style="width:95%"></span>
              <span class="sk" style="width:88%"></span>
              <span class="sk-btn" style="width:44%"></span>
            </div>
            <div class="lp-tpl-use"><span class="lp-btn lp-btn--pri lp-btn--sm">Use template</span></div>
          </div>
          <div class="lp-tpl-meta">
            <div class="lp-tpl-name">{{ t.name }}</div>
            <div class="lp-tpl-desc">{{ t.description }}</div>
          </div>
        </div>
        <div class="lp-tpl-card lp-tpl-card--blank" @click="createBlank">
          <div class="lp-bplus"><CygnusIcon name="plus" /></div>
          <h4>Blank page</h4>
          <p>Start with an empty canvas and build it up block by block.</p>
        </div>
      </div>

      <!-- === YOUR PAGES === -->
      <div class="lp-sec-h lp-sec-h--pages">
        <h3>Your pages</h3>
        <span class="lp-cnt">{{ pages.length }} page{{ pages.length === 1 ? '' : 's' }} · {{ publishedCount }} published</span>
        <span class="lp-spacer"></span>
        <button class="lp-btn lp-btn--ghost lp-btn--sm" :disabled="busy.load" @click="refreshPages">
          <CygnusIcon :name="busy.load ? 'spinner' : 'refresh'" :spin="busy.load" class="me-1" />Refresh
        </button>
      </div>

      <div class="lp-plist">
        <div class="lp-plist-h">
          <span>Page</span><span>URL</span><span>Views</span><span>Submissions</span><span>Conversion</span><span></span>
        </div>
        <div v-for="p in pages" :key="p.id" class="lp-prow">
          <div class="lp-pg-name">
            <div class="lp-pg-nm">
              {{ p.title }}
              <span :class="['lp-pill', `lp-pill--${p.status === 'published' ? 'ok' : 'draft'}`]">{{ p.status === 'published' ? 'Published' : 'Draft' }}</span>
              <small v-if="p.product">{{ p.product }}</small>
            </div>
          </div>
          <div class="lp-pg-url">
            <span class="lp-up">{{ shortUrl(p.url) }}</span>
          </div>
          <div class="lp-pg-num" :class="{ mut: p.status !== 'published' }">{{ p.status === 'published' ? p.views : '—' }}</div>
          <div class="lp-pg-num" :class="{ mut: p.status !== 'published' }">{{ p.status === 'published' ? p.submissions : '—' }}</div>
          <div class="lp-conv">
            <template v-if="p.status === 'published'">
              <div class="lp-conv-v">{{ p.conversion }}%</div>
              <div class="lp-conv-bar"><span :style="{ width: convWidth(p.conversion) }"></span></div>
              <div class="lp-conv-note">views → submissions</div>
            </template>
            <div v-else class="lp-conv-note">Not published</div>
          </div>
          <div class="lp-rowacts">
            <button class="lp-btn lp-btn--sec lp-btn--sm" @click="openBuilder(p.id)">{{ p.status === 'published' ? 'Edit' : 'Continue' }}</button>
            <a v-if="p.status === 'published'" class="lp-btn lp-btn--ghost lp-btn--sm" :href="p.url" target="_blank">View</a>
          </div>
        </div>
        <div v-if="!pages.length && !busy.load" class="lp-empty-row">No pages yet — pick a template above to get started.</div>
      </div>

      <!-- === TRACKING (per-tenant analytics config) === -->
      <div class="lp-sec-h lp-sec-h--pages"><h3>Tracking</h3><span class="lp-cnt">per workspace · injected on your published pages only</span></div>
      <div class="lp-track">
        <div class="lp-track-row">
          <div class="lp-field">
            <label>GA4 measurement ID</label>
            <input class="lp-in lp-in--mono" v-model="tracking.ga4_measurement_id" placeholder="G-XXXXXXXXXX" />
          </div>
          <div class="lp-field">
            <label>Matomo URL</label>
            <input class="lp-in lp-in--mono" v-model="tracking.matomo_url" placeholder="https://analytics.example.com/" />
          </div>
          <div class="lp-field lp-field--narrow">
            <label>Matomo site ID</label>
            <input class="lp-in lp-in--mono" v-model="tracking.matomo_site_id" placeholder="1" />
          </div>
        </div>
        <div class="lp-track-foot">
          <button class="lp-btn lp-btn--pri lp-btn--sm" :disabled="busy.tracking" @click="saveTracking">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.tracking" />Save tracking
          </button>
          <span class="lp-hint">Snippets are injected only on your published landing pages — never in the app, never on drafts.</span>
          <span v-if="trackingMessage" :class="['lp-track-msg', trackingOk ? 'ok' : 'bad']">{{ trackingMessage }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import LandingPageBuilder from "@root/components/LandingPageBuilder.vue";
import templates from "./util/landingTemplates.js";

// Landing pages P1b — the Tools › Landing pages home (Screen A). Container over
// the template gallery + "start from a pipeline" guided pre-fill + the pages
// list (with the P1a list fields) + the per-tenant Tracking card. Editing a
// page swaps in the split-pane builder (LandingPageBuilder) in place, so no new
// shell page/route is needed — the surface stays a single registered component.
export default {
  name: "AdminLandingPages",
  components: { LandingPageBuilder },
  props: { componentData: { type: Object, default: () => ({}) }, apiBase: { type: String, default: "/api/v1" } },
  data() {
    return {
      api: useApi(),
      view: "home",
      builderPageId: null,
      templates,
      pages: [],
      pipelines: [],
      lists: [],
      leadForms: [],
      guidePipelineId: null,
      tracking: { tenant_slug: "", ga4_measurement_id: "", matomo_url: "", matomo_site_id: "" },
      busy: { load: false, create: false, tracking: false },
      error: null,
      trackingMessage: "",
      trackingOk: false,
    };
  },
  computed: {
    publishedCount() { return this.pages.filter((p) => p.status === "published").length; },
    guidePipeline() { return this.pipelines.find((p) => p.id === this.guidePipelineId) || null; },
  },
  mounted() {
    this.refreshPages();
    this.loadPipelines();
    this.loadLists();
    this.loadLeadForms();
    this.loadTracking();
  },
  methods: {
    shortUrl(url) { return (url || "").replace(/^\//, ""); },
    convWidth(pct) { return `${Math.max(0, Math.min(100, (Number(pct) || 0) * 7))}%`; },

    async refreshPages() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get(`${this.apiBase}/admin/landing-pages`);
        const payload = res?.data?.data ?? res?.data ?? res;
        this.pages = Array.isArray(payload) ? payload : (payload?.data || []);
      } catch (e) { this.error = e?.message || "Failed to load landing pages."; }
      this.busy.load = false;
    },
    async loadPipelines() {
      try {
        const res = await this.api.get(`${this.apiBase}/admin/pipelines`);
        const payload = res?.data ?? res;
        this.pipelines = payload?.data || [];
      } catch (e) { this.pipelines = []; }
    },
    async loadLists() {
      try {
        const res = await this.api.get(`${this.apiBase}/admin/lists`);
        const payload = res?.data ?? res;
        this.lists = payload?.data || [];
      } catch (e) { this.lists = []; }
    },
    async loadLeadForms() {
      try {
        const res = await this.api.get(`${this.apiBase}/admin/lead-forms`);
        const payload = res?.data ?? res;
        this.leadForms = payload?.data || [];
      } catch (e) { this.leadForms = []; }
    },
    async loadTracking() {
      try {
        const res = await this.api.get(`${this.apiBase}/admin/landing-pages/tracking`);
        const payload = res?.data ?? res;
        this.tracking = {
          tenant_slug: payload?.tenant_slug || "",
          ga4_measurement_id: payload?.ga4_measurement_id || "",
          matomo_url: payload?.matomo_url || "",
          matomo_site_id: payload?.matomo_site_id || "",
        };
      } catch (e) { /* non-fatal */ }
    },
    async saveTracking() {
      this.busy.tracking = true; this.trackingMessage = ""; this.trackingOk = false;
      try {
        const res = await this.api.put(`${this.apiBase}/admin/landing-pages/tracking`, {
          ga4_measurement_id: this.tracking.ga4_measurement_id || "",
          matomo_url: this.tracking.matomo_url || "",
          matomo_site_id: this.tracking.matomo_site_id || "",
        });
        const payload = res?.data ?? res;
        if (payload && payload.error) { this.trackingMessage = payload.error; }
        else {
          this.tracking = { tenant_slug: payload?.tenant_slug || this.tracking.tenant_slug, ga4_measurement_id: payload?.ga4_measurement_id || "", matomo_url: payload?.matomo_url || "", matomo_site_id: payload?.matomo_site_id || "" };
          this.trackingOk = true; this.trackingMessage = "Tracking saved.";
        }
      } catch (e) {
        this.trackingMessage = e?.response?.data?.error || e?.message || "Save failed.";
      }
      this.busy.tracking = false;
    },

    // ---- create flows ----
    async createPage(body) {
      this.busy.create = true; this.error = null;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/landing-pages`, body);
        const payload = res?.data ?? res;
        if (payload?.page?.id) {
          await this.refreshPages();
          return payload.page;
        }
        this.error = payload?.error || "Create failed.";
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Create failed.";
      } finally {
        this.busy.create = false;
      }
      return null;
    },
    async createFromTemplate(t) {
      const page = await this.createPage({ title: t.name, slug: t.name, blocks_json: t.blocks, product: t.kind });
      if (page) this.openBuilder(page.id);
    },
    async createBlank() {
      const page = await this.createPage({ title: "Untitled page", slug: "untitled-page", blocks_json: [] });
      if (page) this.openBuilder(page.id);
    },
    async createFromPipeline() {
      const p = this.guidePipeline;
      if (!p) return;
      // Hero template, product tag = pipeline kind, then link the pipeline so the
      // builder's AI moment + attribution are wired from the first screen.
      const hero = this.templates.find((t) => t.key === "hero-form") || this.templates[0];
      const page = await this.createPage({ title: p.name + " landing page", slug: p.name + "-landing", blocks_json: hero.blocks, product: p.name });
      if (!page) return;
      try {
        await this.api.post(`${this.apiBase}/admin/landing-pages/${page.id}/pipelines`, { drip_flow_id: p.id, is_default: true });
      } catch (e) { /* link is best-effort; the builder can retry */ }
      this.openBuilder(page.id);
    },

    openBuilder(id) { this.builderPageId = id; this.view = "builder"; },
    closeBuilder() { this.view = "home"; this.builderPageId = null; this.refreshPages(); },
  },
};
</script>

<style scoped>
.musubi-page--landing-pages { padding: var(--m-space-6, 32px); }
.lp-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 22px; }
.lp-head-actions { margin-left: auto; display: flex; gap: 8px; }
.lp-crumb { font-size: .74rem; color: var(--m-text-tertiary); display: flex; gap: 8px; margin-bottom: 6px; }
.lp-crumb b { color: var(--m-text-secondary); font-weight: 600; }
.lp-head h1 { font-size: 1.5rem; letter-spacing: -.02em; font-weight: 800; margin: 0; }
.lp-lede { font-size: .85rem; color: var(--m-text-tertiary); margin: 3px 0 0; }

.lp-guide { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; align-items: center; background: linear-gradient(135deg, var(--m-accent-soft), #F5F3FF); border: 1px solid #DDE1FF; border-radius: 16px; box-shadow: var(--m-shadow-card); padding: 20px 22px; margin-bottom: 26px; }
.lp-guide-top { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
.lp-guide-icn { width: 30px; height: 30px; border-radius: 8px; background: #fff; border: 1px solid #DDE1FF; color: var(--m-accent); display: flex; align-items: center; justify-content: center; box-shadow: var(--m-shadow-card); }
.lp-guide-top h3 { font-size: 1.1rem; margin: 0; letter-spacing: -.01em; }
.lp-guide-sub { font-size: .85rem; color: var(--m-text-secondary); line-height: 1.55; margin: 0; max-width: 44ch; }
.lp-guide-go { display: flex; align-items: center; gap: 10px; margin-top: 13px; }
.lp-guide-then { font-size: .74rem; color: var(--m-text-tertiary); }
.lp-guide-r { background: #fff; border: 1px solid var(--m-border); border-radius: 12px; padding: 15px 16px; box-shadow: var(--m-shadow-card); }
.lp-pick-lbl { font-size: .66rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--m-text-tertiary); margin-bottom: 7px; }
.lp-prefill { margin-top: 12px; background: var(--m-bg-content); border: 1px dashed #C7CEDC; border-radius: 9px; padding: 11px 13px; }
.lp-prefill-l { font-size: .62rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--m-accent); margin-bottom: 6px; }
.lp-prefill-h { font-size: .92rem; font-weight: 700; color: var(--m-text-primary); }
.lp-prefill-note { font-size: .73rem; color: var(--m-text-tertiary); margin-top: 4px; }

.lp-sec-h { display: flex; align-items: baseline; gap: 10px; margin-bottom: 14px; }
.lp-sec-h--pages { margin-top: 30px; }
.lp-sec-h h3 { font-size: 1.08rem; margin: 0; letter-spacing: -.01em; }
.lp-cnt { font-size: .78rem; color: var(--m-text-tertiary); }
.lp-spacer { margin-left: auto; }

.lp-tpl-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.lp-tpl-card { background: #fff; border: 1px solid var(--m-border); border-radius: 12px; box-shadow: var(--m-shadow-card); overflow: hidden; display: flex; flex-direction: column; cursor: pointer; transition: box-shadow .15s, border-color .15s; }
.lp-tpl-card:hover { box-shadow: var(--m-shadow-panel); border-color: var(--m-border-strong); }
.lp-tpl-card:hover .lp-tpl-use { opacity: 1; transform: translateY(0); }
.lp-tpl-thumb { height: 130px; background: var(--m-bg-content); border-bottom: 1px solid var(--m-border); padding: 14px; position: relative; overflow: hidden; }
.lp-skel { display: flex; flex-direction: column; gap: 6px; }
.lp-skel .sk, .lp-skel .sk-d, .lp-skel .sk-btn { display: block; height: 6px; border-radius: 3px; background: #E2E8F0; }
.lp-skel .sk-d { height: 9px; background: #CBD5E1; }
.lp-skel .sk-btn { height: 12px; background: var(--m-accent); opacity: .85; margin-top: 4px; }
.lp-tpl-use { position: absolute; left: 12px; right: 12px; bottom: 12px; opacity: 0; transform: translateY(6px); transition: all .15s; }
.lp-tpl-use .lp-btn { width: 100%; justify-content: center; box-shadow: var(--m-shadow-panel); }
.lp-tpl-meta { padding: 12px 14px 14px; }
.lp-tpl-name { font-size: .9rem; font-weight: 700; letter-spacing: -.01em; }
.lp-tpl-desc { font-size: .75rem; color: var(--m-text-tertiary); margin-top: 3px; line-height: 1.45; }
.lp-tpl-card--blank { border-style: dashed; align-items: center; justify-content: center; text-align: center; padding: 22px 16px; gap: 8px; background: linear-gradient(135deg, #FCFDFF, #F7F8FF); }
.lp-bplus { width: 40px; height: 40px; border-radius: 11px; background: #fff; border: 1px solid var(--m-border); color: var(--m-accent); display: flex; align-items: center; justify-content: center; font-size: 1.2rem; box-shadow: var(--m-shadow-card); }
.lp-tpl-card--blank h4 { font-size: .92rem; margin: 0; }
.lp-tpl-card--blank p { font-size: .74rem; color: var(--m-text-tertiary); margin: 0; max-width: 26ch; }

.lp-plist { border: 1px solid var(--m-border); border-radius: 12px; overflow: hidden; background: #fff; box-shadow: var(--m-shadow-card); }
.lp-plist-h, .lp-prow { display: grid; grid-template-columns: 2.4fr 1.6fr .7fr .9fr 1.1fr auto; gap: 14px; align-items: center; }
.lp-plist-h { padding: 10px 18px; background: var(--m-bg-content); border-bottom: 1px solid var(--m-border); font-size: .62rem; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--m-text-tertiary); }
.lp-prow { padding: 13px 18px; border-bottom: 1px solid var(--m-border); }
.lp-prow:last-child { border-bottom: 0; }
.lp-prow:hover { background: #FCFDFE; }
.lp-pg-nm { font-size: .85rem; font-weight: 700; letter-spacing: -.01em; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.lp-pg-nm small { display: block; width: 100%; font-weight: 500; color: var(--m-text-tertiary); font-size: .72rem; margin-top: 1px; }
.lp-pg-url { font-family: var(--m-font-mono); font-size: .72rem; color: var(--m-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lp-pg-num { font-size: .92rem; font-weight: 800; }
.lp-pg-num.mut { color: var(--m-text-tertiary); font-weight: 600; }
.lp-conv-v { font-size: .8rem; font-weight: 800; }
.lp-conv-bar { height: 6px; border-radius: 999px; background: #EEF2F7; overflow: hidden; margin-top: 5px; }
.lp-conv-bar span { display: block; height: 100%; border-radius: 999px; background: var(--m-accent-2); }
.lp-conv-note { font-size: .62rem; color: var(--m-text-tertiary); margin-top: 4px; }
.lp-rowacts { display: flex; gap: 5px; justify-self: end; }
.lp-empty-row { padding: 24px 18px; text-align: center; color: var(--m-text-tertiary); font-size: .82rem; }

.lp-track { border: 1px solid var(--m-border); border-radius: 12px; background: #fff; box-shadow: var(--m-shadow-card); padding: 16px 18px; }
.lp-track-row { display: grid; grid-template-columns: 1fr 1fr .6fr; gap: 14px; }
.lp-track-foot { display: flex; align-items: center; gap: 12px; margin-top: 14px; flex-wrap: wrap; }
.lp-track-msg { font-size: .76rem; }
.lp-track-msg.ok { color: #0D9488; } .lp-track-msg.bad { color: var(--m-status-danger); }

.lp-field > label { display: block; font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--m-text-secondary); margin-bottom: 5px; }
.lp-in { width: 100%; border: 1px solid var(--m-border); border-radius: 8px; padding: 8px 10px; font: inherit; font-size: .82rem; background: #fff; color: var(--m-text-primary); }
.lp-in:focus { outline: none; border-color: var(--m-accent); }
.lp-in--mono { font-family: var(--m-font-mono); font-size: .78rem; }
.lp-hint { font-size: .72rem; color: var(--m-text-tertiary); }
.mt-2 { margin-top: 8px; }

.lp-btn { display: inline-flex; align-items: center; font: inherit; font-size: .82rem; font-weight: 600; border-radius: 8px; padding: 8px 14px; cursor: pointer; border: 1px solid transparent; text-decoration: none; }
.lp-btn--sm { padding: 6px 11px; font-size: .76rem; }
.lp-btn--pri { background: var(--m-accent); color: #fff; }
.lp-btn--pri:hover { background: var(--m-accent-hover); }
.lp-btn--sec { background: #fff; color: var(--m-text-secondary); border-color: var(--m-border); }
.lp-btn--sec:hover { border-color: var(--m-border-strong); color: var(--m-text-primary); }
.lp-btn--ghost { background: transparent; color: var(--m-text-secondary); }
.lp-btn--ghost:hover { background: var(--m-bg-content); color: var(--m-text-primary); }
.lp-btn:disabled { opacity: .55; cursor: default; }

.lp-pill { display: inline-flex; align-items: center; font-size: .64rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; border-radius: 999px; padding: 2px 8px; }
.lp-pill--ok { background: #E6F6F1; color: #0D9488; }
.lp-pill--draft { background: #F1F5F9; color: var(--m-text-tertiary); }

.lp-alert { padding: 8px 12px; border-radius: 8px; font-size: .8rem; margin-bottom: 14px; }
.lp-alert--danger { background: #FCEDED; color: var(--m-status-danger); }

@media (max-width: 900px) { .lp-guide, .lp-tpl-grid, .lp-track-row { grid-template-columns: 1fr; } }
</style>
